// Display helpers for the Flow of Funds screens. The contract carries numbers
// and ISO dates; every "$1,234" / "4/18/2026" string in the UI comes from here.
// Money is pinned to en-US and dates are read in UTC on purpose: these are USD
// settlement figures and closing dates, so neither may follow the viewer's locale
// or timezone.
import moment from 'moment';
import { ConsiderationTreatment, ConsiderationTreatments } from '../slices/marketplace/pricing/models/consideration-treatment.enum';
import { TypeOfConsideration, TypesOfConsideration } from '../slices/marketplace/pricing/models/type-of-consideration.enum';
import {
    PutCallRight,
    WireTransferGroup,
    WireTransferStatus,
} from '../models/FlowOfFunds/flow-of-funds.model';
import { DeferredPaymentStatus } from '../models/FlowOfFunds/deferred-payments.model';

export const EM_DASH = '—';

/** Money for a ledger cell. `null` reads as an em dash — the value does not apply. */
export const formatMoney = (value: number | null | undefined): string =>
    value == null ? EM_DASH : `$${Math.round(value).toLocaleString('en-US')}`;

/** Money with cents, for wire amounts where the exact figure matters. */
export const formatMoneyExact = (value: number | null | undefined): string =>
    value == null
        ? EM_DASH
        : `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const formatPercent = (value: number | null | undefined): string =>
    value == null ? EM_DASH : `${value}%`;

export const formatMultiple = (value: number | null | undefined): string =>
    value == null ? EM_DASH : `${value}x`;

/** ISO date → M/D/YYYY, the format used across the marketplace screens.
 *  Parses/reads in UTC so a UTC-midnight timestamp doesn't shift a day west of UTC. */
export const formatDate = (iso: string | null | undefined): string => {
    if (!iso) return EM_DASH;
    const date = moment.utc(iso);
    if (!date.isValid()) return EM_DASH;
    return date.format('M/D/YYYY');
};

/** ISO timestamp → "April 18, 2026 · 2:30 PM". Unlike the date-only helpers this is a
 *  true moment in time, so it renders in the viewer's zone rather than UTC. */
export const formatDateTime = (iso: string | null | undefined): string => {
    if (!iso) return EM_DASH;
    const dateTime = moment.utc(iso).local();
    if (!dateTime.isValid()) return EM_DASH;
    return dateTime.format('MMMM D, YYYY · h:mm A');
};

/** Add calendar days to an ISO date and return the M/D/YYYY label. Due dates in
 *  the deferred-payment schedule are all offsets from the process start date.
 *  Adds in UTC so the shift is timezone-stable, matching formatDate. */
export const addDays = (iso: string | null, days: number): string => {
    if (!iso) return 'TBD';
    const date = moment.utc(iso);
    if (!date.isValid()) return 'TBD';
    return formatDate(date.add(days, 'days').toISOString());
};

export const formatFileSize = (bytes: number): string =>
    bytes >= 1_048_576
        ? `${(bytes / 1_048_576).toFixed(1)} MB`
        : `${Math.round(bytes / 1024)} KB`;

const FILE_TYPE_LABELS: Record<string, string> = {
    'application/pdf': 'PDF',
    'application/msword': 'DOC',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'application/vnd.ms-excel': 'XLS',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'text/csv': 'CSV',
    'text/plain': 'TXT',
    'image/png': 'PNG',
    'image/jpeg': 'JPG',
    'image/gif': 'GIF',
};

/** A short readable label for a MIME type, e.g. "PDF" or "DOCX" — never the raw MIME string. */
export const formatFileType = (contentType: string | null | undefined): string => {
    if (!contentType) return 'File';
    const known = FILE_TYPE_LABELS[contentType];
    if (known) return known;
    const subtype = contentType.split('/')[1] ?? contentType;
    const shortSubtype = subtype.split('+')[0].split('.').pop();
    return shortSubtype ? shortSubtype.toUpperCase() : 'File';
};

export const considerationTypeName = (type: TypeOfConsideration): string =>
    TypesOfConsideration.find((x) => x.id === type)?.name ?? '';

export const considerationTreatmentName = (
    treatment: ConsiderationTreatment | null
): string =>
    treatment == null
        ? EM_DASH
        : ConsiderationTreatments.find((x) => x.id === treatment)?.name ?? EM_DASH;

export const PUT_CALL_LABELS: Record<PutCallRight, string> = {
    [PutCallRight.Na]: 'NA',
    [PutCallRight.BuyerCall]: 'Buyer Call',
    [PutCallRight.SellerPut]: 'Seller Put',
    [PutCallRight.BuyerCallAndSellerPut]: 'Buyer Call & Seller Put',
};

export const WIRE_STATUS_LABELS: Record<WireTransferStatus, string> = {
    [WireTransferStatus.PaymentAmountPending]: 'Payment Amount Pending',
    [WireTransferStatus.PaymentAmountAgreed]: 'Payment Amount Agreed',
    [WireTransferStatus.PaymentSent]: 'Payment Sent',
    [WireTransferStatus.PaymentReceived]: 'Payment Received',
};

export const WIRE_STATUS_BADGE: Record<WireTransferStatus, string> = {
    [WireTransferStatus.PaymentAmountPending]: 'ff-wires__status--pending',
    [WireTransferStatus.PaymentAmountAgreed]: 'ff-wires__status--agreed',
    [WireTransferStatus.PaymentSent]: 'ff-wires__status--sent',
    [WireTransferStatus.PaymentReceived]: 'ff-wires__status--received',
};

export const DEFERRED_STATUS_LABELS: Record<DeferredPaymentStatus, string> = {
    [DeferredPaymentStatus.Scheduled]: 'Scheduled',
    [DeferredPaymentStatus.Upcoming]: 'Upcoming',
    [DeferredPaymentStatus.PaymentScheduled]: 'Payment Scheduled',
    [DeferredPaymentStatus.PaymentSent]: 'Payment Sent',
    [DeferredPaymentStatus.PaymentPending]: 'Payment Pending',
    [DeferredPaymentStatus.Paid]: 'Paid',
    [DeferredPaymentStatus.Overpayment]: 'Overpayment',
    [DeferredPaymentStatus.Underpayment]: 'Underpayment',
    [DeferredPaymentStatus.DifferentFromAgreedAmount]: 'Different from agreed amount',
    [DeferredPaymentStatus.ReStructured]: 'Re-Structured',
};

export const wireGroupKey = (
    group: Pick<WireTransferGroup, 'category' | 'sourceRowId'>
): string => `${group.category}-${group.sourceRowId ?? 'single'}`;

export const DEFERRED_STATUS_BADGE: Record<DeferredPaymentStatus, string> = {
    [DeferredPaymentStatus.Scheduled]: 'badge-secondary',
    [DeferredPaymentStatus.Upcoming]: 'badge-secondary',
    [DeferredPaymentStatus.PaymentScheduled]: 'badge-warning',
    [DeferredPaymentStatus.PaymentSent]: 'badge-info',
    [DeferredPaymentStatus.PaymentPending]: 'badge-warning',
    [DeferredPaymentStatus.Paid]: 'badge-success',
    [DeferredPaymentStatus.Overpayment]: 'badge-warning',
    [DeferredPaymentStatus.Underpayment]: 'badge-danger',
    [DeferredPaymentStatus.DifferentFromAgreedAmount]: 'badge-danger',
    [DeferredPaymentStatus.ReStructured]: 'badge-info',
};
